import { useState, useCallback, useEffect, type ReactElement } from 'react';
import { Loader2, Save, X, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PolicyModeSelector } from '@/components/policylist/components/PolicyModeSelector';
import { ArrayObjectSetting } from '@/components/policylist/components/ArrayObjectSetting';
import { PatternArrayModalField } from '@/components/policylist/components/PatternArrayModalField';
import { BetaBadge } from '@/components/policylist/components/BetaBadge';
import {
  shouldUsePatternModal,
  getPatternModalDescription,
  hasBetaBadge,
} from '@/components/policylist/helpers/patternModalRegistry';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import {
  usePolicyEditContext,
  useTogglePolicyVersioned,
  useUpdatePolicySettingsVersioned,
  useResetPolicySettings,
  type PolicyEditScope,
} from '@/hooks/api/usePolicyEditContext';
import {
  PolicyMode,
  type PolicyEditContext,
  type PolicyModeType,
  type PolicySetting,
} from '@kirin/api-client/contracts/policies';
import { useToast } from '@/hooks/use-toast';
import { useIsSupport } from '@/hooks/useRoleCheck';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';
import { isAllowBlockPolicyKey } from '@/components/policylist/allow-block/config';
import { buildPolicySettingsPayload } from '@/components/policylist/allow-block/lib/buildPolicySettingsPayload';
import { useUserPolicySettings } from '@/hooks/api/useUserPolicies';

/** Stored pattern shape (legacy `reason`, newer `description`). */
interface StoredPattern {
  pattern: string;
  reason?: string;
  description?: string;
  severity: SeverityType;
}

/**
 * Build a minimal PolicySetting from the edit context subPolicy data.
 * The edit-context API doesn't return `validation`, so we infer property
 * structure from the array's first item (or defaultValue).
 */
function buildSettingFromEditContext(
  subPolicy: NonNullable<PolicyEditContext['subPolicy']>
): PolicySetting {
  const sampleItem =
    (Array.isArray(subPolicy.value) ? subPolicy.value[0] : null) ??
    (Array.isArray(subPolicy.defaultValue)
      ? subPolicy.defaultValue[0]
      : null) ??
    {};

  const properties: Record<
    string,
    { type: string; required?: boolean; enum?: string[] }
  > = {};
  for (const key of Object.keys(sampleItem)) {
    if (key === 'severity') {
      properties[key] = {
        type: 'string',
        required: true,
        enum: ['low', 'medium', 'high', 'critical'],
      };
    } else {
      properties[key] = { type: 'string', required: true };
    }
  }

  return {
    key: subPolicy.key,
    name: subPolicy.name,
    description: subPolicy.description,
    type: 'array',
    defaultValue: subPolicy.defaultValue,
    value: subPolicy.value,
    isCustomized: subPolicy.isCustomized,
    validation: {
      arrayItemType: 'object',
      required: false,
      properties,
    },
  };
}

interface EditPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: PolicyEditScope;
  policyKey: string;
  subPolicyKey?: string;
  alertKey?: string;
}

export function EditPolicyDialog({
  open,
  onOpenChange,
  scope,
  policyKey,
  subPolicyKey,
  alertKey,
}: EditPolicyDialogProps): ReactElement | null {
  const { toast } = useToast();
  const { changeScopeTo } = useScopeChangeAction();
  const { markDirty, markClean } = useUnsavedChangeActions();
  const scopeId = `edit-policy-${policyKey}`;

  // Account scope is version-less, so optimistic-locking conflict handling
  // only applies to organization-scoped edits.
  const supportsConflict = scope.kind === 'organization';
  const isAccountAllowBlockPolicy =
    scope.kind === 'account' && isAllowBlockPolicyKey(policyKey);

  // useIsSupport(undefined) resolves hasAccess:true (no rolesConfig => "any
  // authenticated user"), so we only trust it when scope is org-scoped.
  const { isSupport } = useIsSupport(
    scope.kind === 'organization' ? scope.organizationId : undefined
  );
  const isSupportReadOnly = scope.kind === 'organization' && isSupport;

  const {
    data: editContext,
    isLoading,
    isError,
    refetch,
  } = usePolicyEditContext(scope, policyKey, subPolicyKey, open);

  const {
    data: accountPolicySettings,
    isLoading: accountSettingsLoading,
    isFetching: accountSettingsFetching,
    isError: accountSettingsError,
    refetch: refetchAccountPolicySettings,
  } = useUserPolicySettings(policyKey, open && isAccountAllowBlockPolicy, {
    // Account saves replace the full settings row — hydrate from server on each open.
    refetchOnMount: 'always',
  });

  const toggleMutation = useTogglePolicyVersioned(scope);
  const settingsMutation = useUpdatePolicySettingsVersioned(scope);

  const [mode, setMode] = useState<PolicyModeType>(PolicyMode.Off);
  const [subPolicyValue, setSubPolicyValue] = useState<unknown>(null);
  const [subPolicyBaseline, setSubPolicyBaseline] = useState<unknown>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasConflict, setHasConflict] = useState(false);

  // Reset local state when the server version changes. This uses the
  // "setState during render" pattern recommended by React docs for syncing
  // derived state — avoids useEffect cascading renders.
  const [syncedVersion, setSyncedVersion] = useState<number | null>(null);
  if (editContext && editContext.version !== syncedVersion) {
    setSyncedVersion(editContext.version);
    setMode(editContext.policy.mode);
    if (editContext.subPolicy) {
      setSubPolicyValue(editContext.subPolicy.value);
      // Baseline snapshot drives the pattern modal's "New/Edited/Deleted"
      // chips against the server value at open time.
      setSubPolicyBaseline(editContext.subPolicy.value);
    }
    setHasChanges(false);
    setHasConflict(false);
  }

  useEffect(() => {
    if (hasChanges) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [hasChanges, markDirty, markClean, scopeId]);

  /** Check whether the sub-policy value has been modified from the server state */
  const isSubPolicyChanged = useCallback(
    (newValue: unknown): boolean => {
      const serverValue = editContext?.subPolicy?.value;
      if (serverValue === undefined || serverValue === null) {
        return false;
      }
      if (typeof newValue === 'boolean' || typeof serverValue === 'boolean') {
        return newValue !== serverValue;
      }
      // Deep comparison for arrays/objects
      return JSON.stringify(newValue) !== JSON.stringify(serverValue);
    },
    [editContext?.subPolicy?.value]
  );

  const handleModeChange = useCallback(
    (newMode: PolicyModeType): void => {
      setMode(newMode);
      setHasChanges(
        newMode !== editContext?.policy.mode ||
          isSubPolicyChanged(subPolicyValue)
      );
    },
    [editContext, subPolicyValue, isSubPolicyChanged]
  );

  const handleSubPolicyChange = useCallback(
    (newValue: unknown): void => {
      setSubPolicyValue(newValue);
      setHasChanges(
        mode !== editContext?.policy.mode || isSubPolicyChanged(newValue)
      );
    },
    [editContext, mode, isSubPolicyChanged]
  );

  const handleSave = useCallback(async (): Promise<void> => {
    if (!editContext) return;

    const version = editContext.version;
    const modeChanged = mode !== editContext.policy.mode;
    const settingChanged =
      editContext.subPolicy && isSubPolicyChanged(subPolicyValue);
    const usesBlockedListMode = isAllowBlockPolicyKey(policyKey);

    if (
      isAccountAllowBlockPolicy &&
      (modeChanged || settingChanged) &&
      (accountSettingsFetching || accountPolicySettings === undefined)
    ) {
      toast({
        title: 'Failed to update policy',
        description: accountSettingsError
          ? 'Could not load current policy settings. Please try again.'
          : 'Policy settings are still loading. Please wait and try again.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const buildAllowBlockSettings = (
        updates: Record<string, unknown>
      ): Record<string, unknown> =>
        isAccountAllowBlockPolicy
          ? buildPolicySettingsPayload(accountPolicySettings, updates)
          : updates;

      if (usesBlockedListMode && modeChanged) {
        // Allow/block policies store user-facing mode in `blockedListMode`
        // (policy settings), not scanner_prefs — mirror AllowBlockPolicySettingsForm.
        const updates: Record<string, unknown> = {
          blockedListMode: mode,
        };
        if (settingChanged && editContext.subPolicy) {
          updates[editContext.subPolicy.key] = subPolicyValue;
        }
        await settingsMutation.mutateAsync({
          settingsUpdate: {
            policyKey,
            settings: buildAllowBlockSettings(updates),
          } as Parameters<
            typeof settingsMutation.mutateAsync
          >[0]['settingsUpdate'],
          expectedVersion: version,
          alertKey,
        });
      } else {
        if (modeChanged) {
          const enabled = mode !== PolicyMode.Off;
          await toggleMutation.mutateAsync({
            policyKey,
            enabled,
            mode,
            expectedVersion: version,
            alertKey,
          });
        }

        if (settingChanged && editContext.subPolicy) {
          // If mode was toggled first, the server bumped the combined version by 1.
          // Pass the incremented version so the settings call doesn't 409.
          const settingsVersion = modeChanged ? version + 1 : version;
          const updates: Record<string, unknown> = {
            [editContext.subPolicy.key]: subPolicyValue,
          };
          await settingsMutation.mutateAsync({
            settingsUpdate: {
              policyKey,
              settings: usesBlockedListMode
                ? buildAllowBlockSettings(updates)
                : updates,
            } as Parameters<
              typeof settingsMutation.mutateAsync
            >[0]['settingsUpdate'],
            expectedVersion: settingsVersion,
            alertKey,
          });
        }
      }

      toast({
        title: 'Policy updated successfully.',
      });
      setHasChanges(false);
      markClean(scopeId);
      onOpenChange(false);
    } catch (error: unknown) {
      const status =
        error != null &&
        typeof error === 'object' &&
        'status' in error &&
        typeof (error as { status: unknown }).status === 'number'
          ? (error as { status: number }).status
          : undefined;

      if (status === 409) {
        setHasConflict(true);
        void refetch();
      } else {
        const reason =
          error != null &&
          typeof error === 'object' &&
          'message' in error &&
          typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : 'An unexpected error occurred.';
        toast({
          title: 'Failed to update policy',
          description: reason,
          variant: 'destructive',
        });
      }
    }
  }, [
    editContext,
    mode,
    subPolicyValue,
    policyKey,
    alertKey,
    toggleMutation,
    settingsMutation,
    toast,
    onOpenChange,
    refetch,
    markClean,
    scopeId,
    isSubPolicyChanged,
    isAccountAllowBlockPolicy,
    accountPolicySettings,
    accountSettingsError,
    accountSettingsFetching,
  ]);

  const isSaving = toggleMutation.isPending || settingsMutation.isPending;

  const resetMutation = useResetPolicySettings(scope);

  const handleResetToDefaults = useCallback(async (): Promise<void> => {
    if (!editContext?.subPolicy) return;
    try {
      await resetMutation.mutateAsync({
        policyKey,
        settingKey: editContext.subPolicy.key,
      });
      toast({
        title: 'Policy updated successfully.',
      });
      void refetch();
    } catch {
      toast({
        title: 'Failed to update policy',
        description: 'Could not reset settings to defaults.',
        variant: 'destructive',
      });
    }
  }, [editContext, policyKey, resetMutation, toast, refetch]);

  const isResetting = resetMutation.isPending;
  const isBusy = isSaving || isResetting;

  const resetToServerState = useCallback((): void => {
    if (editContext) {
      setMode(editContext.policy.mode);
      if (editContext.subPolicy) {
        setSubPolicyValue(editContext.subPolicy.value);
      }
      setHasChanges(false);
    }
    markClean(scopeId);
  }, [editContext, markClean, scopeId]);

  const closeDialog = useCallback((): void => {
    resetToServerState();
    onOpenChange(false);
  }, [resetToServerState, onOpenChange]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (nextOpen) {
        onOpenChange(true);
        return;
      }
      if (isBusy) return;
      if (!hasChanges) {
        closeDialog();
        return;
      }
      changeScopeTo({
        from: scopeId,
        id: `close-${scopeId}`,
        title: 'Discard unsaved changes?',
        body: 'You have unsaved changes. If you leave now, your changes will be lost.',
        cancelLabel: 'Stay on Policy',
        discardLabel: 'Leave Policy',
        run: closeDialog,
      });
    },
    [closeDialog, onOpenChange, isBusy, hasChanges, changeScopeTo, scopeId]
  );

  // Build the header: "Edit Policy: PolicyName > SubPolicyName" or "Edit Policy: PolicyName"
  const headerTitle = editContext
    ? editContext.subPolicy
      ? `Edit Policy: ${editContext.policy.name} > ${editContext.subPolicy.name}`
      : `Edit Policy: ${editContext.policy.name}`
    : 'Edit Policy';

  const showResetButton =
    editContext?.subPolicy && editContext.subPolicy.isCustomized;

  const isArraySubPolicy = editContext?.subPolicy?.type === 'array';
  const usesPatternModal =
    isArraySubPolicy &&
    editContext?.subPolicy &&
    shouldUsePatternModal(policyKey, editContext.subPolicy.key);

  // Beta chip parity with the Policies screen — driven by the shared registry
  // so Alerts and Policies never disagree on which sub-policies are Beta.
  const showSubPolicyBeta =
    !!editContext?.subPolicy &&
    hasBetaBadge(policyKey, editContext.subPolicy.key);

  const accountSettingsHydrating =
    isAccountAllowBlockPolicy &&
    (accountSettingsLoading || accountSettingsFetching);

  const accountSettingsUnavailable =
    isAccountAllowBlockPolicy &&
    !accountSettingsHydrating &&
    (accountSettingsError || accountPolicySettings === undefined);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={isArraySubPolicy ? 'sm:max-w-3xl' : 'sm:max-w-lg'}
        onInteractOutside={e => e.preventDefault()}
        onOverlayPointerDown={() => handleOpenChange(false)}
      >
        <DialogHeader>
          <DialogTitle>{headerTitle}</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <div className="py-4 text-sm text-destructive">
            Failed to load policy context. Please try again.
          </div>
        )}

        {editContext && !isLoading && (
          <div className="space-y-4">
            {/* Policy card — name, description, mode selector */}
            <div className="rounded-md border p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">
                  {editContext.policy.name}
                </Label>
                <PolicyModeSelector
                  value={mode}
                  onChange={handleModeChange}
                  disabled={isBusy || isSupportReadOnly}
                  supportsEnforce={editContext.policy.supportsEnforce}
                  enforceDisabledReason={
                    editContext.policy.enforceDisabledReason
                  }
                  enforceTooltip={editContext.policy.enforceTooltip}
                  variant="contrast"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {editContext.policy.description}
              </p>
            </div>

            {/* Sub-policy toggle (boolean settings) */}
            {editContext.subPolicy &&
              editContext.subPolicy.type === 'boolean' && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Label className="text-sm font-medium">
                      {editContext.subPolicy.name}
                    </Label>
                    {showSubPolicyBeta && <BetaBadge />}
                    {editContext.subPolicy.description && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground text-xs">
                            &#9432;
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {editContext.subPolicy.description}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={subPolicyValue === true}
                      onCheckedChange={handleSubPolicyChange}
                      disabled={isBusy || mode === PolicyMode.Off}
                    />
                    <span className="text-sm text-muted-foreground">
                      {subPolicyValue ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              )}

            {/* Sub-policy array setting */}
            {editContext.subPolicy &&
              editContext.subPolicy.type === 'array' &&
              Array.isArray(subPolicyValue) && (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Label className="text-sm font-medium">
                      {editContext.subPolicy.name}
                    </Label>
                    {showSubPolicyBeta && <BetaBadge />}
                    {editContext.subPolicy.description && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground text-xs">
                            &#9432;
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          {editContext.subPolicy.description}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {usesPatternModal ? (
                    <PatternArrayModalField
                      value={subPolicyValue as StoredPattern[]}
                      onChange={handleSubPolicyChange}
                      disabled={isBusy || mode === PolicyMode.Off}
                      readOnly={isSupportReadOnly}
                      isSupportReadOnly={isSupportReadOnly}
                      policyKey={policyKey}
                      policyName={editContext.subPolicy.name}
                      policyDescription={getPatternModalDescription(
                        policyKey,
                        editContext.subPolicy.key,
                        editContext.subPolicy.description
                      )}
                      baseline={
                        Array.isArray(subPolicyBaseline)
                          ? (subPolicyBaseline as StoredPattern[])
                          : []
                      }
                    />
                  ) : (
                    <ArrayObjectSetting
                      setting={buildSettingFromEditContext(
                        editContext.subPolicy
                      )}
                      value={subPolicyValue as Record<string, unknown>[]}
                      onChange={handleSubPolicyChange}
                      disabled={
                        isBusy || mode === PolicyMode.Off || isSupportReadOnly
                      }
                    />
                  )}
                </div>
              )}
          </div>
        )}

        {/* Conflict warning (organization scope only — account is version-less) */}
        {supportsConflict && hasConflict && (
          <p className="text-sm text-destructive">
            This policy was updated from another surface or session. Please
            reload the action before saving.
          </p>
        )}

        {accountSettingsUnavailable && (
          <p className="text-sm text-destructive">
            Failed to load policy settings required to save changes.{' '}
            <button
              type="button"
              className="underline font-medium"
              onClick={() => void refetchAccountPolicySettings()}
            >
              Try again
            </button>
          </p>
        )}

        {/* Footer: Save + Cancel left, Reset to Defaults right */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={
                !hasChanges ||
                isBusy ||
                isLoading ||
                hasConflict ||
                isSupportReadOnly ||
                (isAccountAllowBlockPolicy &&
                  (accountSettingsHydrating || accountSettingsUnavailable))
              }
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={closeDialog}
              disabled={isBusy}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>

          {showResetButton && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={isBusy || isSupportReadOnly}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset to Defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset Policy Settings?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset all settings for &quot;
                    {editContext?.policy.name}&quot; back to their default
                    values. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetToDefaults}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Reset Settings
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
